// Clients management — the master list of clients (Phase 1 normalization).
// Data comes from the cached useClients() hook; create/update/delete invalidate
// the ["clients"] query to refresh. Domains, servers, and invoices link here.
import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import { useThemeColors } from "../context/ThemeContext";
import GlassCard from "../components/GlassCard";
import { glassRowSx } from "../components/glassRow";
import { HEADING_FONT } from "../theme/fonts";
import api from "../api";
import { useClients } from "../hooks/queries";
import { useToast } from "../components/ToastProvider";
import { useConfirm } from "../components/ConfirmProvider";
import { useTableSelection } from "../hooks/useTableSelection";
import SelectionActionBar from "../components/SelectionActionBar";
import { AuthContext } from "../context/AuthContext";

const CLIENT_GRADIENT = "linear-gradient(135deg, #818cf8 0%, #6366f1 100%)";

function Clients() {
  const navigate = useNavigate();
  const colors = useThemeColors();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const { hasPermission } = useContext(AuthContext);
  const canAdd = hasPermission("clients", "add");
  const canEdit = hasPermission("clients", "edit");
  const canDelete = hasPermission("clients", "delete");

  const { data: clientsData } = useClients();
  const clients = clientsData || [];

  // --- Add / Edit (full pages) ---

  function openAddDialog() {
    navigate("/add-client");
  }

  function openEditDialog(client) {
    navigate(`/clients/${client.id}/edit`);
  }

  // --- Delete ---

  async function handleDelete(id) {
    try {
      await api.delete(`/clients/${id}`);
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Client deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete client");
    }
  }

  // --- Row selection + bulk delete ---
  const pageIds = clients.map((c) => c.id);
  const {
    selectedIds,
    toggleRow,
    toggleAll,
    clear: clearSelection,
    allSelected,
    someSelected,
  } = useTableSelection(pageIds);
  const [bulkBusy, setBulkBusy] = useState(false);

  async function handleDeleteSelected() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const ok = await confirm({
      title: `Delete ${ids.length} client${ids.length === 1 ? "" : "s"}?`,
      message:
        "The selected clients will be removed. Linked domains, servers and invoices keep their data — only the client link is cleared.",
      danger: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/clients/bulk-delete", { ids });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      clearSelection();
      toast.success(`Deleted ${res?.deleted ?? ids.length} client(s)`);
    } catch (err) {
      toast.error(err.message || "Failed to delete selected clients");
    } finally {
      setBulkBusy(false);
    }
  }

  // checkbox | # | Name | Company | Email | Phone | GSTIN | Location | Actions
  const GRID_COLUMNS =
    "44px 56px 1.3fr 1.1fr 1.3fr 130px 140px 1fr 110px";

  return (
    <DashboardLayout>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: HEADING_FONT,
              fontWeight: 700,
              color: colors.text,
              letterSpacing: "-0.5px",
            }}
          >
            Clients
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textMuted, mt: 0.5 }}>
            {clients.length} {clients.length === 1 ? "client" : "clients"}
          </Typography>
        </Box>

        {canAdd && (
          <Button
            onClick={openAddDialog}
            startIcon={<AddIcon />}
            sx={{
              alignSelf: { xs: "flex-end", sm: "center" },
              ml: { sm: "auto" },
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              px: 2.5,
              py: 1.1,
              borderRadius: 2,
              color: "#fff",
              background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
              boxShadow: "0 4px 14px rgba(79, 70, 229, 0.35)",
              "&:hover": {
                boxShadow: "0 8px 22px rgba(79, 70, 229, 0.45)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.15s ease",
            }}
          >
            Add Client
          </Button>
        )}
      </Stack>

      {/* Bulk-delete selection bar */}
      <SelectionActionBar
        count={selectedIds.size}
        onDelete={canDelete ? handleDeleteSelected : undefined}
        onClear={clearSelection}
        busy={bulkBusy}
        colors={colors}
      />

      {/* Table */}
      <GlassCard sx={{ overflow: "hidden" }}>
        {/* Horizontal scroll wrapper — full table on mobile, swipe sideways */}
        <Box sx={{ overflowX: "auto" }}>
        {/* Table header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: GRID_COLUMNS,
            minWidth: { xs: "max-content", md: "auto" },
            gap: 2,
            px: 3,
            py: 1.75,
            borderBottom: `1px solid ${colors.divider}`,
            background: colors.hover,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleAll}
              sx={{ p: 0, color: colors.textMuted }}
            />
          </Box>
          {["#", "Name", "Company", "Email", "Phone", "GSTIN", "Location", "Actions"].map((h) => (
            <Typography
              key={h}
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: colors.textMuted,
                textAlign: "left",
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {/* Rows */}
        {clients.length === 0 ? (
          <Box sx={{ py: 8, px: 3, textAlign: "center" }}>
            <ContactsOutlinedIcon
              sx={{ fontSize: 44, color: colors.textFaint, mb: 1 }}
            />
            <Typography
              sx={{ fontFamily: HEADING_FONT, fontSize: 15, fontWeight: 600, color: colors.text }}
            >
              No clients yet
            </Typography>
            <Typography sx={{ fontSize: 13, color: colors.textMuted, mt: 0.5 }}>
              Click "Add Client" to create your first one
            </Typography>
          </Box>
        ) : (
          clients.map((client, idx) => (
            <Box
              key={client.id}
              sx={{
                display: "grid",
                gridTemplateColumns: GRID_COLUMNS,
                minWidth: { xs: "max-content", md: "auto" },
                gap: 2,
                px: 3,
                py: 1.75,
                alignItems: "center",
                borderBottom:
                  idx < clients.length - 1
                    ? `1px solid ${colors.divider}`
                    : "none",
                ...glassRowSx({ idx, accent: CLIENT_GRADIENT, hoverBg: colors.hover }),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={selectedIds.has(client.id)}
                  onChange={() => toggleRow(client.id)}
                  sx={{ p: 0, color: colors.textMuted }}
                />
              </Box>
              <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted }}>
                {idx + 1}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    background: CLIENT_GRADIENT,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 4px 12px rgba(99, 102, 241, 0.35)",
                  }}
                >
                  <ContactsOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{ fontFamily: HEADING_FONT, fontSize: 14, fontWeight: 600, color: colors.text }}
                  >
                    {client.name}
                  </Typography>
                  {client.clientCode ? (
                    <Typography noWrap sx={{ fontSize: 12, color: colors.textMuted }}>
                      Code: {client.clientCode}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
              <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted }}>
                {client.companyName || "—"}
              </Typography>
              <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted }}>
                {client.email || "—"}
              </Typography>
              <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted }}>
                {client.phone || "—"}
              </Typography>
              <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted }}>
                {client.gstin || "—"}
              </Typography>
              <Typography noWrap sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted }}>
                {client.locationName || client.state || client.country || "—"}
              </Typography>
              <Stack direction="row" spacing={0.5} justifyContent="flex-start">
                {canEdit && (
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => openEditDialog(client)}
                      sx={{
                        color: colors.textMuted,
                        "&:hover": { background: colors.hover, color: "#818cf8" },
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={async () => {
                        if (
                          await confirm({
                            title: "Delete client?",
                            message:
                              "Linked domains, servers and invoices keep their data — only the client link is cleared.",
                            danger: true,
                            confirmLabel: "Delete",
                          })
                        )
                          handleDelete(client.id);
                      }}
                      sx={{
                        color: colors.textMuted,
                        "&:hover": {
                          background: "rgba(239, 68, 68, 0.1)",
                          color: "#ef4444",
                        },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          ))
        )}
        </Box>
      </GlassCard>

    </DashboardLayout>
  );
}

export default Clients;
